import React, { useState } from "react";
import { AlertCircle, Mail, CheckCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useEnvironment } from "../../hooks/useEnvironment";

const ForgotPasswordPage: React.FC = () => {
  const { selfServeSignup } = useEnvironment();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);

    try {
      const apiBase =
        typeof window !== "undefined" &&
        window.location.hostname === "localhost"
          ? "http://localhost:8000/api/"
          : import.meta.env.VITE_API_URL ||
            "https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/";

      const response = await fetch(`${apiBase}auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to send reset email");
      }

      setSuccess(true);
      console.log("Password reset email sent to:", email);
    } catch (error: any) {
      console.error("Password reset error:", error);
      setError(
        error.message || "Failed to send reset email. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-xl p-12 md:p-16">
        <div className="flex flex-col items-center mb-8">
          <div className="mb-6 cursor-pointer">
            <img
              src="/landing/getpawtected.svg"
              alt="GetPawtected"
              className="w-64 md:w-80 lg:w-96 h-auto"
            />
          </div>
          <h1 className="text-4xl md:text-3xl font-medium font-poppins text-black mb-4 text-center">
            Reset Your Password
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 text-center font-inter leading-relaxed max-w-md">
            Enter your email address and we'll send the correct next step based
            on your account status.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-6 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-base text-red-700 font-inter">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-2xl flex items-start gap-4">
            <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-base text-green-700 font-inter font-medium mb-2">
                Check Your Email
              </p>
              <p className="text-sm text-green-600 font-inter">
                The message sent to{" "}
                <span className="font-semibold">{email}</span> may contain
                password reset instructions or an email-verification link. Check
                your spam folder if you don't see it.
              </p>
            </div>
          </div>
        )}

        {!success ? (
          <form onSubmit={handleResetPassword} className="space-y-6">
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2 font-inter"
              >
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors font-inter text-base"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-5 px-8 bg-[color:var(--pmc-accent)] text-white rounded-full hover:bg-[color:var(--pmc-accent-hover)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-poppins text-lg font-medium"
            >
              {loading ? (
                <>
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <span>Send Instructions</span>
              )}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <button
              onClick={() => {
                setSuccess(false);
                setEmail("");
              }}
              className="w-full py-5 px-8 bg-[color:var(--pmc-accent)] text-white rounded-full hover:bg-[color:var(--pmc-accent-hover)] transition-all duration-200 font-poppins text-lg font-medium"
            >
              Send Another Email
            </button>
          </div>
        )}

        {/* Back to Login Link */}
        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-base text-[color:var(--pmc-accent)] hover:underline font-inter font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </div>

        {/* Self-serve sign-up: dev/local only; hard-gated on staging/prod. */}
        {selfServeSignup && (
          <div className="mt-8 text-center">
            <p className="text-base text-gray-600 font-inter">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="text-[color:var(--pmc-accent)] hover:underline font-medium"
              >
                Sign up
              </Link>
            </p>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-gray-400 font-inter text-sm">
            Pet Registration & Insurance Management System
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
